import { useState } from 'react';
import { User, Mail, Phone, MapPin, Briefcase, Calendar, Camera } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

interface UserProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function UserProfileModal({ isOpen, onClose }: UserProfileModalProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: 'Administrador',
    email: 'admin@sistema.com',
    phone: '+52 55 1234 5678',
    location: 'Ciudad de México, México',
    role: 'Administrador del Sistema',
    joinDate: '15 de Enero, 2024',
    bio: 'Responsable de la gestión y administración del sistema médico.',
  });

  const handleSave = () => {
    toast.success('Perfil actualizado exitosamente');
    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
    // Reset form data if needed
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
        <DialogHeader>
          <DialogTitle className="text-gray-900 dark:text-gray-100">Mi Perfil</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Avatar Section */}
          <div className="flex flex-col items-center space-y-4">
            <div className="group relative">
              <Avatar className="h-32 w-32 ring-4 ring-pink-100 dark:ring-pink-900/50">
                <AvatarFallback className="bg-gradient-to-br from-pink-600 to-rose-600 text-3xl text-white">
                  AD
                </AvatarFallback>
              </Avatar>
              {isEditing && (
                <button className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50 opacity-0 transition-opacity group-hover:opacity-100">
                  <Camera className="h-8 w-8 text-white" />
                </button>
              )}
            </div>
            <div className="text-center">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">{formData.name}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">{formData.role}</p>
            </div>
          </div>

          {/* Form Fields */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label
                htmlFor="name"
                className="flex items-center gap-2 text-gray-700 dark:text-gray-300"
              >
                <User className="h-4 w-4 text-pink-600 dark:text-pink-400" />
                Nombre Completo
              </Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                disabled={!isEditing}
                className="border-gray-200 bg-gray-50 disabled:opacity-70 dark:border-gray-700 dark:bg-gray-800"
              />
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="email"
                className="flex items-center gap-2 text-gray-700 dark:text-gray-300"
              >
                <Mail className="h-4 w-4 text-pink-600 dark:text-pink-400" />
                Correo Electrónico
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                disabled={!isEditing}
                className="border-gray-200 bg-gray-50 disabled:opacity-70 dark:border-gray-700 dark:bg-gray-800"
              />
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="phone"
                className="flex items-center gap-2 text-gray-700 dark:text-gray-300"
              >
                <Phone className="h-4 w-4 text-pink-600 dark:text-pink-400" />
                Teléfono
              </Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                disabled={!isEditing}
                className="border-gray-200 bg-gray-50 disabled:opacity-70 dark:border-gray-700 dark:bg-gray-800"
              />
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="location"
                className="flex items-center gap-2 text-gray-700 dark:text-gray-300"
              >
                <MapPin className="h-4 w-4 text-pink-600 dark:text-pink-400" />
                Ubicación
              </Label>
              <Input
                id="location"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                disabled={!isEditing}
                className="border-gray-200 bg-gray-50 disabled:opacity-70 dark:border-gray-700 dark:bg-gray-800"
              />
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="role"
                className="flex items-center gap-2 text-gray-700 dark:text-gray-300"
              >
                <Briefcase className="h-4 w-4 text-pink-600 dark:text-pink-400" />
                Rol
              </Label>
              <Input
                id="role"
                value={formData.role}
                disabled
                className="border-gray-200 bg-gray-50 opacity-70 dark:border-gray-700 dark:bg-gray-800"
              />
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="joinDate"
                className="flex items-center gap-2 text-gray-700 dark:text-gray-300"
              >
                <Calendar className="h-4 w-4 text-pink-600 dark:text-pink-400" />
                Fecha de Ingreso
              </Label>
              <Input
                id="joinDate"
                value={formData.joinDate}
                disabled
                className="border-gray-200 bg-gray-50 opacity-70 dark:border-gray-700 dark:bg-gray-800"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="bio" className="text-gray-700 dark:text-gray-300">
              Biografía
            </Label>
            <Textarea
              id="bio"
              value={formData.bio}
              onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
              disabled={!isEditing}
              rows={3}
              className="resize-none border-gray-200 bg-gray-50 disabled:opacity-70 dark:border-gray-700 dark:bg-gray-800"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-800">
            {isEditing ? (
              <>
                <Button
                  variant="outline"
                  onClick={handleCancel}
                  className="border-gray-300 text-gray-700 hover:bg-gray-100 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleSave}
                  className="bg-gradient-to-r from-pink-600 to-rose-600 text-white hover:from-pink-700 hover:to-rose-700"
                >
                  Guardar Cambios
                </Button>
              </>
            ) : (
              <Button
                onClick={() => setIsEditing(true)}
                className="bg-gradient-to-r from-pink-600 to-rose-600 text-white hover:from-pink-700 hover:to-rose-700"
              >
                Editar Perfil
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
